/**
 * Entity pickers: searchable selects backed by list endpoints.
 *
 * All pickers share `EntityPicker`, which renders a native `<select>` (keyboard
 * and RTL friendly) preceded by an optional search box for large sets. Options
 * are cached per query key so repeated dialogs are instant.
 */
import { useQuery } from "@tanstack/react-query";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type Schemas } from "@/api/client";
import { errorMessage, unwrap } from "@/api/problem";
import { Input, Select } from "@/shared/ui/form";
import { SearchCombobox } from "@/shared/components/search-combobox";

/** A selectable option. */
export interface PickerOption {
  id: string;
  label: string;
  /** Optional secondary text shown after the label. */
  hint?: string;
}

/** Props shared by all pickers. */
export interface PickerProps {
  id: string;
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  required?: boolean;
  disabled?: boolean;
  /** Show a search box above the select (for large lists). */
  searchable?: boolean;
  /** Placeholder for the empty option. */
  placeholder?: string;
  "aria-invalid"?: boolean;
  "aria-label"?: string;
}

/** Generic picker over a loader function. */
export function EntityPicker({
  id,
  value,
  onChange,
  required,
  disabled,
  searchable,
  placeholder,
  queryKey,
  load,
  ...rest
}: PickerProps & {
  queryKey: readonly unknown[];
  load: (q: string) => Promise<PickerOption[]>;
}): ReactElement {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const query = useQuery({
    queryKey: [...queryKey, searchable ? q : ""],
    queryFn: () => load(q),
    staleTime: 60_000,
  });
  const options = query.data ?? [];
  // Keep the current value selectable even if it is not in the filtered list.
  const hasCurrent = !value || options.some((o) => o.id === value);
  return (
    <div className="space-y-2">
      {searchable ? (
        <Input
          aria-label={t("app.search")}
          placeholder={t("app.search")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          disabled={disabled}
        />
      ) : null}
      <Select
        id={id}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        required={required}
        disabled={(disabled ?? false) || query.isPending}
        aria-invalid={rest["aria-invalid"]}
        aria-label={rest["aria-label"]}
      >
        <option value="">{placeholder ?? "—"}</option>
        {!hasCurrent && value ? <option value={value}>{value}</option> : null}
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
            {o.hint ? ` · ${o.hint}` : ""}
          </option>
        ))}
      </Select>
      {query.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(query.error)}
        </p>
      ) : null}
    </div>
  );
}

/** Parties, optionally filtered by role (vendor, manufacturer, customer…). */
export function PartyPicker({
  role,
  combobox,
  selectedLabel,
  ...props
}: PickerProps & {
  role?: string;
  combobox?: boolean;
  selectedLabel?: string;
}): ReactElement {
  if (combobox) {
    return (
      <SearchCombobox
        {...props}
        selectedLabel={selectedLabel}
        queryKey={["picker", "parties", role]}
        load={async (q) =>
          unwrap(
            await api.GET("/api/v1/parties/", {
              params: { query: { q: q || undefined, role, limit: 50 } },
            }),
          ).items.map((party) => ({ id: party.id, label: party.name, hint: party.code }))
        }
        loadSelected={async (id) => {
          const party = unwrap(
            await api.GET("/api/v1/parties/{party_id}", { params: { path: { party_id: id } } }),
          );
          return { id: party.id, label: party.name, hint: party.code };
        }}
      />
    );
  }
  return (
    <EntityPicker
      {...props}
      searchable
      queryKey={["picker", "parties", role]}
      load={async (q) =>
        unwrap(
          await api.GET("/api/v1/parties/", {
            params: { query: { q: q || undefined, role, limit: 50 } },
          }),
        ).items.map((p: Schemas["PartyOut"]) => ({ id: p.id, label: p.name, hint: p.code }))
      }
    />
  );
}

/** Sites. */
export function SitePicker(props: PickerProps): ReactElement {
  return (
    <EntityPicker
      {...props}
      queryKey={["picker", "sites"]}
      load={async () =>
        unwrap(await api.GET("/api/v1/locations/sites")).map((s: Schemas["SiteOut"]) => ({
          id: s.id,
          label: s.name,
          hint: s.code,
        }))
      }
    />
  );
}

/** Locations, optionally within a site and/or stock-only. */
export function LocationPicker({
  siteId,
  stockOnly,
  ...props
}: PickerProps & { siteId?: string | null; stockOnly?: boolean }): ReactElement {
  return (
    <EntityPicker
      {...props}
      queryKey={["picker", "locations", siteId ?? "", stockOnly ?? false]}
      load={async () =>
        unwrap(
          await api.GET("/api/v1/locations/locations", {
            params: {
              query: { site_id: siteId ?? undefined, stock_only: stockOnly ? true : undefined },
            },
          }),
        ).map((l: Schemas["LocationOut"]) => ({ id: l.id, label: l.name, hint: l.code }))
      }
    />
  );
}

/** Asset types; `componentsOnly`/`vesselsOnly` narrow the list. */
export function AssetTypePicker({
  componentsOnly,
  vesselsOnly,
  parentTypeId,
  ...props
}: PickerProps & {
  componentsOnly?: boolean;
  vesselsOnly?: boolean;
  parentTypeId?: string;
}): ReactElement {
  return (
    <EntityPicker
      {...props}
      queryKey={[
        "picker",
        "asset-types",
        componentsOnly ?? false,
        vesselsOnly ?? false,
        parentTypeId,
      ]}
      load={async () =>
        unwrap(await api.GET("/api/v1/assets/types"))
          .filter((t: Schemas["AssetTypeOut"]) => t.is_active)
          .filter((t: Schemas["AssetTypeOut"]) => (componentsOnly ? t.is_component : true))
          .filter((t: Schemas["AssetTypeOut"]) => (vesselsOnly ? t.is_vessel : true))
          .filter(
            (t: Schemas["AssetTypeOut"]) =>
              !parentTypeId || (t.is_component && t.allowed_parent_type_ids.includes(parentTypeId)),
          )
          .map((t: Schemas["AssetTypeOut"]) => ({ id: t.id, label: t.name, hint: t.key }))
      }
    />
  );
}

/** Make/models, optionally for one asset type. */
export function ModelPicker({
  assetTypeId,
  ...props
}: PickerProps & { assetTypeId?: string | null }): ReactElement {
  return (
    <EntityPicker
      {...props}
      queryKey={["picker", "models", assetTypeId ?? ""]}
      load={async () =>
        unwrap(
          await api.GET("/api/v1/assets/models", {
            params: { query: { asset_type_id: assetTypeId ?? undefined } },
          }),
        )
          .filter((m: Schemas["ModelOut"]) => m.is_active)
          .map((m: Schemas["ModelOut"]) => ({
            id: m.id,
            label: m.name,
            hint: m.manufacturer_name ?? undefined,
          }))
      }
    />
  );
}

/** Assets (searchable). */
export function AssetSelect({
  vesselsOnly,
  excludeId,
  installTypeId,
  installAssetId,
  ...props
}: PickerProps & {
  vesselsOnly?: boolean;
  excludeId?: string;
  installTypeId?: string;
  installAssetId?: string;
}): ReactElement {
  return (
    <EntityPicker
      {...props}
      searchable
      queryKey={[
        "picker",
        "assets",
        vesselsOnly ?? false,
        excludeId ?? "",
        installTypeId,
        installAssetId,
      ]}
      load={async (q) =>
        unwrap(
          await api.GET("/api/v1/assets/", {
            params: {
              query: {
                q: q || undefined,
                vessels_only: vesselsOnly ? true : undefined,
                limit: 50,
                install_type_id: installTypeId,
                install_asset_id: installAssetId,
              },
            },
          }),
        )
          .items.filter((a: Schemas["AssetOut"]) => a.id !== excludeId)
          .map((a: Schemas["AssetOut"]) => ({ id: a.id, label: a.name, hint: a.code }))
      }
    />
  );
}

/** Inventory items (searchable). */
export function ItemPicker(props: PickerProps): ReactElement {
  return (
    <EntityPicker
      {...props}
      searchable
      queryKey={["picker", "items"]}
      load={async (q) =>
        unwrap(
          await api.GET("/api/v1/inventory/items", {
            params: { query: { q: q || undefined, limit: 50 } },
          }),
        ).items.map((i: Schemas["ItemOut"]) => ({ id: i.id, label: i.name, hint: i.sku }))
      }
    />
  );
}

/** Active members (searchable). */
export function MemberPicker(props: PickerProps): ReactElement {
  return (
    <EntityPicker
      {...props}
      searchable
      queryKey={["picker", "members"]}
      load={async (q) =>
        unwrap(
          await api.GET("/api/v1/identity/members", {
            params: { query: { q: q || undefined, status: "active", limit: 50 } },
          }),
        ).items.map((m: Schemas["MemberOut"]) => ({
          id: m.id,
          label: m.user.display_name || m.user.email,
          hint: m.user.display_name ? m.user.email : undefined,
        }))
      }
    />
  );
}

/** Job templates. */
export function JobTemplatePicker(props: PickerProps): ReactElement {
  return (
    <EntityPicker
      {...props}
      queryKey={["picker", "job-templates"]}
      load={async () =>
        unwrap(await api.GET("/api/v1/maintenance/job-templates")).items.map(
          (t: Schemas["JobTemplateOut"]) => ({ id: t.id, label: t.name, hint: t.code }),
        )
      }
    />
  );
}

/** Meter types. */
export function MeterTypePicker(props: PickerProps): ReactElement {
  return (
    <EntityPicker
      {...props}
      queryKey={["picker", "meter-types"]}
      load={async () =>
        unwrap(await api.GET("/api/v1/meters/types")).map((m: Schemas["MeterTypeOut"]) => ({
          id: m.id,
          label: m.name,
          hint: m.unit,
        }))
      }
    />
  );
}

/** Document categories. */
export function DocumentCategoryPicker(props: PickerProps): ReactElement {
  return (
    <EntityPicker
      {...props}
      queryKey={["picker", "document-categories"]}
      load={async () =>
        unwrap(await api.GET("/api/v1/documents/categories")).map((c: Schemas["CategoryOut"]) => ({
          id: c.id,
          label: c.name,
          hint: c.key,
        }))
      }
    />
  );
}
